import { ChartCard } from "@/components/waste/reports/chart-card";
import type { WasteStatsBucket } from "@/hooks/use-waste-stats";
import { useTranslation } from "@/i18n/use-translation";
import { formatNumber } from "@/lib/format";
import { l1ReasonAction, l1ReasonLabel } from "@/lib/l1-diagnostic-reasons";

/**
 * L1 Breakdown (POS2-755): why events stayed unidentified (tier L1), ordered by
 * frequency, each with a one-line action and its share of total L1 events.
 * Reasons/actions come from a code constant (no AI call).
 */
export function L1BreakdownWidget({ buckets }: { buckets: WasteStatsBucket[] }) {
  const { locale, t } = useTranslation();
  const total = buckets.reduce((sum, b) => sum + b.event_count, 0);
  const rows = [...buckets].sort((a, b) => b.event_count - a.event_count);

  return (
    <ChartCard
      title={t("dashboard.widget.l1Breakdown")}
      description={t("dashboard.l1Description")}
      isEmpty={total === 0}
      emptyLabel={t("dashboard.l1Empty")}
    >
      <ul className="flex flex-col divide-y divide-black/[0.06]" data-testid="l1-breakdown-list">
        {rows.map((b) => {
          const pct = total > 0 ? (b.event_count / total) * 100 : 0;
          const action = l1ReasonAction(t, b.key);
          return (
            <li
              key={b.key}
              className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {l1ReasonLabel(t, b.key)}
                </p>
                {action && <p className="mt-0.5 text-xs text-gray-500">{action}</p>}
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold tabular-nums text-gray-900">
                  {pct.toFixed(0)}%
                </p>
                <p className="text-xs tabular-nums text-gray-400">
                  {formatNumber(locale, b.event_count)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </ChartCard>
  );
}
